import { dpow } from '../core/dmath';
import { paradoxDef } from '../content/paradox';

/**
 * Progression formulas — single source of truth (mirrored in docs/DESIGN.md).
 */

/** XP required to go from level L to L+1. */
export function xpToNext(level: number): number {
  return Math.floor(16 + 10 * dpow(level, 1.3)); // dpow: identical bits on every JS engine (lockstep)
}

/** Enemy stat scaling by global stage index (0 = era 1 stage 1). */
export function stageScaling(globalStage: number, paradox: number): { hp: number; damage: number; speed: number; xp: number; bossHp: number; hazardDamage: number } {
  const era = Math.floor(globalStage / 3);
  const mutators = paradoxDef(paradox);
  const hp = (1 + 0.18 * globalStage) * (1 + 0.15 * era) * mutators.enemyHp;
  const damage = (1 + 0.07 * globalStage) * mutators.enemyDamage;
  const speed = Math.min(1.5, (1 + 0.02 * globalStage) * mutators.enemySpeed);
  const xp = 1 + 0.12 * globalStage;
  return { hp, damage, speed, xp, bossHp: mutators.bossHp, hazardDamage: mutators.hazardDamage };
}

/** Starting level for anchored runs (skip eras → start weaker than a full run would be). */
export function anchorStartLevel(eraIndex: number): number {
  return Math.max(1, 4 + eraIndex * 5);
}

export interface RunSummaryForReward {
  erasCleared: number;
  bossesKilled: number;
  timeSurvived: number; // seconds
  kills: number;
  noHitStages: number;
  paradox: number;
  startEra: number; // anchored start index (0 = full run)
  victory: boolean;
  shardsPickedUp: number;
}

/**
 * Chronoshard reward at run end.
 *   base    = 25·erasCleared + 40·bossesKilled + timeSurvived/20 + kills/15 + 15·noHitStages
 *   ×paradoxProfile.shardReward  ×(victory ? 1.5 : 1)  ×anchorPenalty(0.6^startEra… floored at 0.35)
 *   + shards picked up during the run (never penalized)
 */
export function shardReward(r: RunSummaryForReward, shardMult = 1): number {
  const base = 25 * r.erasCleared + 40 * r.bossesKilled + r.timeSurvived / 20 + r.kills / 15 + 15 * r.noHitStages;
  const anchorPenalty = Math.max(0.35, dpow(0.6, r.startEra));
  const total = base * paradoxDef(r.paradox).shardReward * (r.victory ? 1.5 : 1) * anchorPenalty * shardMult;
  return Math.round(total + r.shardsPickedUp);
}

/** Score for leaderboards. */
export function runScore(r: { kills: number; erasCleared: number; bossesKilled: number; level: number; paradox: number; timeSurvived: number }): number {
  return Math.round((r.kills * 10 + r.erasCleared * 5000 + r.bossesKilled * 2500 + r.level * 100) * paradoxDef(r.paradox).score - r.timeSurvived);
}

/** Rarity weights shifted by luck (0 = default). */
export function rarityWeights(luck: number): [number, number, number, number] {
  const l = Math.max(0, luck);
  return [Math.max(10, 60 - 20 * l), 28 + 8 * l, 10 + 8 * l, 2 + 4 * l];
}

/** Cost of meta upgrade tier t (0-based) for a def. */
export function metaCost(baseCost: number, growth: number, tier: number): number {
  return Math.round(baseCost * dpow(growth, tier));
}
